import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Field, Form, Formik } from 'formik';
import { BsFunnelFill } from 'react-icons/bs';
import EpFormSelect, { changeSelectValue } from '../EpFormSelect';
import EpButton from '../EpButton';
import indicatorsService from '../../services/indicatorsService';
import { storeLastFiltersApplied } from '../../redux/actions/filterAction';
import useTranslation from '../../hooks/useTranslation';
import filterService from '../../services/filterService';
import './styles.scss';

const EpFilter = ({
  selectPatientsGroupsOptions, selectPatientsGroupsDefaultValue, lastFiltersApplied,
  setConsolidatedTotalizerIndicatorsDataFunc, setConsolidatedIndicatorsDataFunc,
  setEvolutiveIndicatorsDataFunc, setIsLoadingPageFunc,
  browsableFilterSelectRef, patientsGroupsSelectRef,
  filterIndicatorsDefaultOptionObj, filterYearsDefaultOptionObj, setFilterYearsDefaultOptionObjFunc,
}) => {
  // Tags do Globalization
  const Report = useTranslation('Report');
  const Consolidated = useTranslation('Consolidated');
  const Evolutive = useTranslation('Evolutive');
  const PatientGroups = useTranslation('Patient Groups');
  const Select = useTranslation('Select');
  const Apply = useTranslation('Apply');

  const dispatch = useDispatch();
  const currentUserLanguage = localStorage.getItem('bi.userLanguage');
  const filterCache = useSelector(state => state.filter.filterCache);

  const [filterReportTypeSelected, setFilterReportTypeSelected] = useState(
    lastFiltersApplied ? lastFiltersApplied.reportType : 0,
  );
  const [filterPatientGroupSelected, setFilterPatientGroupSelected] = useState(
    lastFiltersApplied ? lastFiltersApplied.patientGroupItemCode : 0,
  );

  // Valores iniciais dos campos do filtro
  const filterInitialValues = {
    reportType: '0',
    patientsGroups: selectPatientsGroupsDefaultValue,
  };

  const updateFiltersAppliedStorage = (inputName, selectedValue) => {
    const newFiltersAppliedObj = {
      ...lastFiltersApplied,
      [inputName]: selectedValue,
    };

    dispatch(storeLastFiltersApplied(newFiltersAppliedObj));
  };

  // Método para tratar aplicação do filtro
  const handleFilterSubmit = async (values, actions) => {
    setIsLoadingPageFunc(true);

    // Busca novas opções do filtro local de anos
    const newFilterYearsOptions = await filterService().getYearsByPatientGroup(
      currentUserLanguage, filterCache,
      lastFiltersApplied.patientGroupItemCode,
    );

    // Define o novo valor padrão conforme opções do filtro local de anos
    const newFilterYearsDefaultOptionObj = newFilterYearsOptions
      ? newFilterYearsOptions[0] : null;

    setFilterYearsDefaultOptionObjFunc(newFilterYearsDefaultOptionObj);

    let queryStrings = {
      selectedPatientGroup: lastFiltersApplied.patientGroupItemCode,
      selectedIndicator: lastFiltersApplied.indicator,
    };

    if (filterReportTypeSelected === 0) {
      updateFiltersAppliedStorage('reportType', 0);

      queryStrings = {
        selectedYear: lastFiltersApplied.year,
        selectedPatientGroup: lastFiltersApplied.patientGroupItemCode,
      };

      // Reseta filtro local para o valor padrão
      if (filterReportTypeSelected !== lastFiltersApplied.reportType
        || filterPatientGroupSelected !== lastFiltersApplied.patientGroupItemCode) {
        queryStrings.selectedYear = newFilterYearsDefaultOptionObj
          ? newFilterYearsDefaultOptionObj.value : filterYearsDefaultOptionObj.value;

        changeSelectValue(
          browsableFilterSelectRef,
          newFilterYearsDefaultOptionObj || filterYearsDefaultOptionObj,
        );
      }

      const consolidatedIndicatorsTotalizersResponseJSON = await indicatorsService()
        .getConsolidatedTotalizers(queryStrings);
      const consolidatedIndicatorsTotalizersList = consolidatedIndicatorsTotalizersResponseJSON
      && consolidatedIndicatorsTotalizersResponseJSON.data
        ? consolidatedIndicatorsTotalizersResponseJSON.data.response : null;

      const consolidatedIndicatorsResponseJSON = await indicatorsService()
        .getConsolidatedIndicators(queryStrings);
      const consolidatedIndicatorsList = consolidatedIndicatorsResponseJSON
      && consolidatedIndicatorsResponseJSON.data
        ? consolidatedIndicatorsResponseJSON.data.response : null;

      setConsolidatedTotalizerIndicatorsDataFunc(consolidatedIndicatorsTotalizersList || null);
      setConsolidatedIndicatorsDataFunc(consolidatedIndicatorsList || null);
      setFilterPatientGroupSelected(lastFiltersApplied.patientGroupItemCode);
    } else {
      updateFiltersAppliedStorage('reportType', 1);

      // Reseta filtro local para o valor padrão
      if (filterReportTypeSelected !== lastFiltersApplied.reportType
          || filterPatientGroupSelected !== lastFiltersApplied.patientGroupItemCode) {
        queryStrings.selectedIndicator = filterIndicatorsDefaultOptionObj.value;
        changeSelectValue(browsableFilterSelectRef, filterIndicatorsDefaultOptionObj);
      }

      const evolutiveIndicatorsResponseJSON = await indicatorsService()
        .getEvolutiveIndicators(queryStrings);
      const evolutiveIndicatorsList = evolutiveIndicatorsResponseJSON
      && evolutiveIndicatorsResponseJSON.data
        ? evolutiveIndicatorsResponseJSON.data.response : null;

      setEvolutiveIndicatorsDataFunc(evolutiveIndicatorsList || null);
      setFilterPatientGroupSelected(lastFiltersApplied.patientGroupItemCode);
    }

    setIsLoadingPageFunc(false);
    actions.setSubmitting(false);
  };

  return (
    <div className="filter">
      <div className="filter__icon"><BsFunnelFill /></div>

      <Formik
        initialValues={filterInitialValues}
        onSubmit={(values, actions) => handleFilterSubmit(values, actions)}
      >
        {({
          handleSubmit, handleChange, setFieldValue, values,
        }) => (
          <Form className="filter__form">
            <div id="filter__fields--report" className="filter__fields">
              <label className="filter__fields-label" htmlFor="filter__fields-radio-list--report">{Report}</label>
              <ul id="filter__fields-radio-list--report" className="filter__fields-radio-list">

                {/* Relatório - Radio - Opção 1 */}
                <li className="filter__fields-radio-list-item form-check form-check-inline">
                  <label
                    className="filter__fields-radio-list-item-label form-check-label"
                    htmlFor="filter__fields-radio-list-item-input--consolidated"
                  >
                    <Field
                      type="radio"
                      name="reportType"
                      id="filter__fields-radio-list-item-input--consolidated"
                      className="filter__fields-radio-list-item-input form-check-input"
                      value="0"
                      onClick={() => setFilterReportTypeSelected(0)}
                    />
                    {Consolidated}
                  </label>
                </li>

                {/* Relatório - Radio - Opção 2 */}
                <li className="filter__fields-radio-list-item form-check form-check-inline">
                  <label
                    className="filter__fields-radio-list-item-label form-check-label"
                    htmlFor="filter__fields-radio-list-item-input--evolutive"
                  >
                    <Field
                      type="radio"
                      name="reportType"
                      id="filter__fields-radio-list-item-input--evolutive"
                      className="filter__fields-radio-list-item-input form-check-input"
                      value="1"
                      onClick={() => setFilterReportTypeSelected(1)}
                    />
                    {Evolutive}
                  </label>
                </li>
              </ul>
            </div>

            <div id="filter__fields--patient-groups" className="filter__fields">
              <label className="filter__fields-label" htmlFor="filter__fields-radio-list--report">{PatientGroups}</label>

              <EpFormSelect
                inputName="patientsGroups"
                inputId="filter__select--patients-groups"
                customRef={patientsGroupsSelectRef}
                placeholderText={Select}
                selectValue={values.patientsGroups}
                selectDefaultValue={selectPatientsGroupsDefaultValue}
                customClass="filter__fields-select"
                onChangeFunc={(option) => {
                  handleChange('patientsGroups');
                  setFieldValue('patientsGroups', option);
                  updateFiltersAppliedStorage('patientGroupItemCode', option.value);
                }}
                selectOptions={selectPatientsGroupsOptions}
              />
            </div>

            <EpButton
              actionType="submit"
              customClass="filter__fields-button"
              buttonText={Apply}
              onClickFunc={handleSubmit}
              buttonType="primary"
              buttonSize="sm"
            />
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default EpFilter;

EpFilter.defaultProps = {
  selectPatientsGroupsOptions: null,
  selectPatientsGroupsDefaultValue: null,
  patientsGroupsSelectRef: null,
  browsableFilterSelectRef: null,
  filterIndicatorsDefaultOptionObj: null,
  filterYearsDefaultOptionObj: null,
};

EpFilter.propTypes = {
  selectPatientsGroupsOptions: PropTypes.arrayOf(PropTypes.any),
  selectPatientsGroupsDefaultValue: PropTypes.objectOf(PropTypes.any),
  lastFiltersApplied: PropTypes.objectOf(PropTypes.any).isRequired,
  setConsolidatedTotalizerIndicatorsDataFunc: PropTypes.func.isRequired,
  setConsolidatedIndicatorsDataFunc: PropTypes.func.isRequired,
  setEvolutiveIndicatorsDataFunc: PropTypes.func.isRequired,
  setIsLoadingPageFunc: PropTypes.func.isRequired,
  patientsGroupsSelectRef: PropTypes.objectOf(PropTypes.any),
  browsableFilterSelectRef: PropTypes.objectOf(PropTypes.any),
  filterIndicatorsDefaultOptionObj: PropTypes.objectOf(PropTypes.any),
  filterYearsDefaultOptionObj: PropTypes.objectOf(PropTypes.any),
  setFilterYearsDefaultOptionObjFunc: PropTypes.func.isRequired,
};
